import { parseDomain } from '../../utils';
import { PremiumPurchasePlatformModule } from '@wix/premium-purchase-platform/@sdk';
import { PremiumCartApiClient } from '@wix/premium-cart-sdk/@premium-cart-api-client';
import { dynamicLoadApiQueries } from './dynamicLoadApiQueries';
import { transformDomainLineItems } from '../../utils/DomainLineItemsUtils';
import { DomainTypeId, DomainAddonsTypeId } from '../../typing/domainProducts';
export const addonToProductId = {
  privacy_and_dnssec: 'f8211619-d9f6-4312-9d03-f2958bbd08aa',
  public: 'b9d89ff0-f29b-4bfd-a3f0-6e34ae65120d',
  privacy: '22a84545-4ac0-4490-a434-45a1ebc479fb'
};
export const getDomainLineItem = async _ref => {
  var _offerings$products;
  let httpClient = _ref.httpClient,
    request = _ref.request;
  const cycle = request.cycle,
    domain = request.domain,
    core = request.core;
  const parsedDomain = parseDomain(domain);
  const PurchasePlatformApiClient = PremiumPurchasePlatformModule.SDK.ApiClient;
  if (parsedDomain.error || !parsedDomain.tld) {
    throw new Error('Bad domain name');
  }
  const offerings = await PurchasePlatformApiClient.query.getPurchaseUserOffering({
    httpClient,
    request: {
      productTypeId: DomainTypeId,
      purchaseContext: {
        params: {
          tld: `.${parsedDomain.tld}`
        }
      }
    }
  });
  const domainProduct = (_offerings$products = offerings.products) == null ? void 0 : _offerings$products[0];
  if (!domainProduct) {
    throw new Error("Can't find product");
  }
  return {
    cycle,
    metadata: {
      domainName: parsedDomain.asciiDomain,
      core: core ? 'true' : 'false',
      flow: 'purchase'
    },
    productInfo: {
      ...domainProduct,
      productTypeId: DomainTypeId
    }
  };
};
export const getDomainAddonLineItem = async _ref2 => {
  var _offerings$products2;
  let httpClient = _ref2.httpClient,
    request = _ref2.request;
  const cycle = request.cycle,
    addon = request.addon,
    domain = request.domain,
    customOfferingFilters = request.customOfferingFilters;
  const parsedDomain = parseDomain(domain);
  const _PremiumPurchasePlatf = PremiumPurchasePlatformModule.SDK.ApiClient.queries({
      httpClient
    }),
    getRecommendedProductFamily = _PremiumPurchasePlatf.getRecommendedProductFamily,
    getPurchaseUserOffering = _PremiumPurchasePlatf.getPurchaseUserOffering;
  if (parsedDomain.error || !parsedDomain.tld) {
    throw new Error('Bad domain name');
  }
  const addonRequest = {
    productTypeId: DomainAddonsTypeId,
    purchaseContext: {
      params: {
        tld: `.${parsedDomain.tld}`
      }
    }
  };
  if (!addon) {
    const recommendedDomainAddon = await getRecommendedProductFamily(addonRequest);
    if (!recommendedDomainAddon.productFamilyId) {
      throw new Error("Can't find product");
    }
    return {
      cycle,
      productInfo: {
        productId: recommendedDomainAddon.productFamilyId,
        productTypeId: DomainAddonsTypeId
      },
      metadata: {
        domainName: parsedDomain.asciiDomain
      },
      ...(customOfferingFilters && {
        customOfferingFilters
      })
    };
  }
  const offerings = await getPurchaseUserOffering(addonRequest);
  const domainAddon = (_offerings$products2 = offerings.products) == null ? void 0 : _offerings$products2.find(_ref3 => {
    let productId = _ref3.productId;
    return addonToProductId[addon] === productId;
  });
  if (!domainAddon) {
    throw new Error("Can't find product");
  }
  return {
    cycle,
    productInfo: domainAddon,
    metadata: {
      domainName: parsedDomain.asciiDomain
    },
    ...(customOfferingFilters && {
      customOfferingFilters
    })
  };
};
export const getDomainLineItemWithAddon = async _ref4 => {
  let httpClient = _ref4.httpClient,
    request = _ref4.request;
  const cycle = request.cycle,
    domain = request.domain,
    addon = request.addon,
    core = request.core,
    customOfferingFilters = request.customOfferingFilters;
  const parsedDomain = parseDomain(domain);
  const queries = await dynamicLoadApiQueries(httpClient);
  if (parsedDomain.error || !parsedDomain.tld) {
    throw new Error('Bad domain name');
  }
  const _await$Promise$allSet = await Promise.allSettled([queries.getDomainLineItem({
      domain: parsedDomain,
      core,
      cycle
    }), queries.getDomainAddonLineItem({
      cycle,
      domain: parsedDomain,
      addon,
      customOfferingFilters
    })]),
    domainLineItem = _await$Promise$allSet[0],
    domainAddonLineItem = _await$Promise$allSet[1];
  const result = transformDomainLineItems(domainLineItem, domainAddonLineItem, cycle);
  return {
    domainLineItem: result.domainLineItem,
    ...((result == null ? void 0 : result.domainAddonLineItem) && {
      domainAddonLineItem: result.domainAddonLineItem
    })
  };
};
export const getDomainAddons = async _ref5 => {
  let httpClient = _ref5.httpClient,
    request = _ref5.request;
  const domain = request.domain;
  const parsedDomain = domain ? parseDomain(domain) : null;
  const PurchasePlatformApiClient = PremiumPurchasePlatformModule.SDK.ApiClient;
  const filterByTld = parsedDomain ? {
    tld: `.${parsedDomain.tld}`
  } : {};
  const offerings = await PurchasePlatformApiClient.query.getPurchaseUserOffering({
    httpClient,
    request: {
      productTypeId: DomainAddonsTypeId,
      purchaseContext: {
        params: filterByTld
      }
    }
  });
  return offerings;
};
export const upsertDomainToCart = async _ref6 => {
  let httpClient = _ref6.httpClient,
    request = _ref6.request;
  const _PremiumCartApiClient = PremiumCartApiClient.queries({
      httpClient
    }),
    upsertLineItemToActiveCart = _PremiumCartApiClient.upsertLineItemToActiveCart;
  const lineItem = await getDomainLineItem({
    httpClient,
    request
  });
  const cartResponse = await upsertLineItemToActiveCart({
    lineItem
  });
  return cartResponse;
};
export const upsertDomainAddonToCart = async _ref7 => {
  let httpClient = _ref7.httpClient,
    request = _ref7.request;
  const _PremiumCartApiClient2 = PremiumCartApiClient.queries({
      httpClient
    }),
    upsertLineItemToActiveCart = _PremiumCartApiClient2.upsertLineItemToActiveCart;
  const lineItem = await getDomainAddonLineItem({
    httpClient,
    request
  });
  const cartResponse = await upsertLineItemToActiveCart({
    lineItem
  });
  return cartResponse;
};
//# sourceMappingURL=cartAggregations.js.map